import React, { useEffect, useState, useRef } from 'react';
import { Menu, MenuItem } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { useRecoilValue } from 'recoil';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { userAtom } from '@/state/atom/userAtom';

import PlusIcon from '@/component/common/icons/PlusIcon';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import FileIcon from '@/component/common/icons/FileIcon';
import MCPIcon from '@/component/common/icons/MCPIcon';
import LogDetailIcon from '@/component/common/icons/LogDetailIcon';
import KBoardIcon from '@/component/common/icons/KBoardIcon';
import ReferenceKnowledgeIcon from '@/component/common/icons/ReferenceKnowledgeIcon';
import AnalysisIcon from '@/component/common/icons/AnalysisIcon';

import { MENU_ITEM_TYPE, MENU_ITEM_KEY } from './handler/GroupButtonsHandler';
import MCPServerMenu from './components/MCPServerMenu';

import './styles.css';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import OpenAiUtil from '@/common/utils/OpenAiUtil';
import Logger from '@/common/utils/Logger';
import { handleToggleMcpServer, isShowMCPServerDropdown, isShowKibanaAnalysisOption } from './handler/MCPServerDropdownHandler';
import { isShowButtonLog, openAiLogDetail, updateLogURL } from './handler/AiLogDetailHandler';
import { handleConnectKboard, isShowKBoardConnect, routeKboardResult  } from './handler/KBoardConnectHandler';
import { checkImageCaptureSupport } from './handler/UploadImageHandler';
import { toggleUsingReferenceKnowledge } from './handler/ReferenceKnowledgeHandle';
import { formListOfProjectSelector, fieldListOfProjectSelector } from '@/state/selector/formSelector';
import { useSnackbar } from 'notistack';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

function InputChatAiGroupButtons(props) {
    useTranslationSubscription();
    
    let {
        API,
        chatThreadId,
        // Model (used to check multimodal support)
        usingChatGptModel,
        usingReasoningEffort,

        // UploadFile
        onTriggerUploadFile,
        isFileTranslationEnabled,

        // ImageCapture
        onTriggerImageCapture,

        // ReferenceKnowledge
        isUsingKnowledgeService,
        toggleUsingKnowledgeTicketList,
        setToggleUsingKnowledgeTicketList,
        panelId,
        tabId,
        panelList,
        setPanelList,

        // MCPServerDropdown
        selectedCorporationDetail,
        serverList,
        selectedMcpServers = [],
        onMcpSelectionChange,

        // AiLogDetail
        listMessageContent,
        isAiAssistantLog,

        // KBoard sendAIChat setters
        setListMessageContent,
        setIsEnableBtnSend,
        setIsShowButtonStop,
        setIsOnlyRunChatAI,
        setIsStopStreamContent,
        setIdOfResponseMessage,
        setIsDisabledInputChat,
        filterKnowledgeListData,
        setUsingChatGptModel,
        setUsingReasoningEffort
    } = props;

    const [anchorEl, setAnchorEl] = useState(null);
    const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
    const [activeSubMenuKey, setActiveSubMenuKey] = useState(null);
    const [logUrl, setLogUrl] = useState('');

    const parentMenuRef = useRef(null);
    const mcpMenuItemRef = useRef(null);

    const corporationDetail = useRecoilValue(corporationAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const currentUserDetail = useRecoilValue(userAtom);
    const formListState = useRecoilValue(formListOfProjectSelector);
    const fieldListState = useRecoilValue(fieldListOfProjectSelector);
    const { enqueueSnackbar } = useSnackbar();
    const isMenuOpen = Boolean(anchorEl);
    const isSubMenuOpen = Boolean(subMenuAnchorEl) &&
        [MENU_ITEM_KEY.AI_CHAT_MENU_MCP_SETTING].includes(activeSubMenuKey);

    // Fetch logUrl when chatThreadId changes
    useEffect(() => {
        if (!chatThreadId) return;
        let isMounted = true;

        updateLogURL({chatThreadId, setLogUrl, isMounted});

        return () => {
            isMounted = false;
        };
    }, [chatThreadId]);

    // Get list group buttons by condition
    const getListGroupBtns = () => {
        let items = [];
        if (!chatThreadId) return items;
        
        // validate & push item: UploadFile (accepts both images and documents)
        const isCanUploadImgToSendAi = OpenAiUtil.isSupportMultiModal(usingChatGptModel, usingReasoningEffort);
        const isCanUploadFile = isCanUploadImgToSendAi || isFileTranslationEnabled;
        if (isCanUploadFile) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_UPLOAD_FILE,
                type: MENU_ITEM_TYPE.ACTION,
                icon: <FileIcon width={16} height={14} />,
            });
        }

        // validate & push item: ImageCapture
        const isSupportImageCapture = checkImageCaptureSupport();
        if (isCanUploadImgToSendAi && isSupportImageCapture) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_CAPTURE_IMAGE,
                type: MENU_ITEM_TYPE.ACTION,
                icon: <CameraAltIcon fontSize="small" htmlColor="#5f6368" />,
            });
        }

        // validate & push item: usingReferenceKnowledge
        if (isUsingKnowledgeService) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_USING_REFERENCE_KNOWLEDGE,
                label: 'AI_CHAT_MENU_REFERENCE_KNOWLEDGE',
                type: MENU_ITEM_TYPE.TOGGLE,
                icon: <ReferenceKnowledgeIcon width={18} height={18} isUsing={false}/>,
                checked: toggleUsingKnowledgeTicketList,
            });
        }

        // validate & push item: MCP
        const isShowMCPDropdown = isShowMCPServerDropdown({corporationDetail, selectedCorporationDetail, serverList});
        if (isShowMCPDropdown) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_MCP_SETTING,
                type: MENU_ITEM_TYPE.SUBMENU,
                icon: <MCPIcon width={19} height={10} />,
            });
        }

        // validate & push item: KibanaAnalysis (分析機能)
        const isShowKibanaOption = isShowKibanaAnalysisOption({corporationDetail, projectInuseDetail});
        if (isShowKibanaOption) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_KIBANA_ANALYSIS,
                label: 'AI_CHAT_MENU_KIBANA_ANALYSIS',
                type: MENU_ITEM_TYPE.ACTION,
                icon: <AnalysisIcon width={16} height={16} />,
                selected: (selectedMcpServers || []).includes(Constant.KIBANA_MCP_ID),
            });
        }

        // validate & push item: Connect K-board
        const isOnConnectKboard = isShowKBoardConnect({
            corporationDetail,
            projectIdKnowledgeSelected: filterKnowledgeListData?.projectIdKnowledgeSelected,
            projectInuseDetail
        });
        if (isOnConnectKboard) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_CONNECT_KBOARD,
                type: MENU_ITEM_TYPE.ACTION,
                icon: <KBoardIcon />,
            });
        }

        // validate & push item: AiLogDetail
        const isShowAiLogDetail = logUrl &&
            !isAiAssistantLog &&
            isShowButtonLog({corporationDetail, projectInuseDetail, currentUserDetail, listMessageContent});
        if (isShowAiLogDetail) {
            items.push({
                key: MENU_ITEM_KEY.AI_CHAT_MENU_AI_LOG_DETAIL,
                type: MENU_ITEM_TYPE.ACTION,
                icon: <LogDetailIcon width={16} height={16} />,
            });
        }

        return items;
    };
    const listGroupBtns = getListGroupBtns();

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSubMenuAnchorEl(null);
        setActiveSubMenuKey(null);
    };

    const handleCloseSubMenu = (event) => {
        setSubMenuAnchorEl(null);
        setActiveSubMenuKey(null);

        // Check if click landed on the parent menu's Popover area
        if (event && parentMenuRef?.current) {
            const rect = parentMenuRef.current.getBoundingClientRect();
            const { clientX, clientY } = event;

            const isMouseClickOnParentMenu = clientX >= rect.left && clientX <= rect.right &&
                clientY >= rect.top && clientY <= rect.bottom
            if (isMouseClickOnParentMenu) return; // Click is within parent menu → don't close it
        }
        setAnchorEl(null);
    };

    const handleMenuItemClick = async (item, event) => {
        // Handle submenu items
        if (item.type === MENU_ITEM_TYPE.SUBMENU) {
            event.stopPropagation();
            event.preventDefault();
            if (item.key === MENU_ITEM_KEY.AI_CHAT_MENU_MCP_SETTING) {
                setSubMenuAnchorEl(mcpMenuItemRef.current);
                setActiveSubMenuKey(MENU_ITEM_KEY.AI_CHAT_MENU_MCP_SETTING);
            }
            return;
        }

        switch (item.key) {
            case MENU_ITEM_KEY.AI_CHAT_MENU_CAPTURE_IMAGE:
                onTriggerImageCapture?.();
                break;
            case MENU_ITEM_KEY.AI_CHAT_MENU_UPLOAD_FILE:
                onTriggerUploadFile?.();
                break;
            case MENU_ITEM_KEY.AI_CHAT_MENU_AI_LOG_DETAIL:
                await openAiLogDetail(chatThreadId);
                break;
            case MENU_ITEM_KEY.AI_CHAT_MENU_CONNECT_KBOARD:
                await handleConnectKboard({
                    API,
                    corporationDetail,
                    projectInuseDetail: projectInuseDetail,
                    currentUserDetail,
                    aiThreadChatId: chatThreadId,
                    onKboardResult: (payload) => {
                        // TODO: Process kboard result - trigger logic or call API
                        routeKboardResult(payload?.payload, payload?.handleConnectData);
                    },
                    onStatusChange: status => {
                        Logger.info('[InputChatAiGroupButtons] KBoard status:', status);
                    },
                    handleConnectData: {
                        API: API,
                        usingChatGptModel: usingChatGptModel,
                        usingReasoningEffort: usingReasoningEffort,
                        formList: formListState,
                        fieldList: fieldListState,
                        chatThreadId: chatThreadId,
                        filterKnowledgeListData: filterKnowledgeListData,
                        userMail: currentUserDetail?.mail || '',
                        setListMessageContent,
                        setIsEnableBtnSend,
                        setIsShowButtonStop,
                        setIsOnlyRunChatAI,
                        setIsStopStreamContent,
                        setIdOfResponseMessage,
                        setIsDisabledInputChat,
                        corporationDetail,
                        currentUserDetail,
                        enqueueSnackbar,
                        setUsingChatGptModel,
                        setUsingReasoningEffort
                    }
                });
                break;
            case MENU_ITEM_KEY.AI_CHAT_MENU_USING_REFERENCE_KNOWLEDGE:
                toggleUsingReferenceKnowledge(
                    panelList,
                    setPanelList,
                    panelId,
                    tabId,
                    setToggleUsingKnowledgeTicketList,
                );
                await Utils.delay(300);
                return; // Don't close menu for toggle items
            case MENU_ITEM_KEY.AI_CHAT_MENU_KIBANA_ANALYSIS:
                handleToggleMcpServer({
                    selectedServerId: Constant.KIBANA_MCP_ID,
                    selectedMcpServers,
                    onMcpSelectionChange
                });
                break;
            default:
                break;
        }

        handleCloseMenu();
    };

    return (
        <div className="input-chat-ai-group-buttons">
            {/* Plus Button Trigger */}
            <button
                className="btn-reset-default ai-chat-plus-btn"
                disabled={Utils.isEmptyArray(listGroupBtns)}
                onClick={handleOpenMenu}
            >
                <PlusIcon width={20} height={20} />
            </button>

            {/* Main Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                className="ai-chat-group-menu"
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                getContentAnchorEl={null}
                MenuListProps={{
                    ref: parentMenuRef,
                    autoFocusItem: false,
                }}
            >
                {!Utils.isEmptyArray(listGroupBtns) && listGroupBtns.map(item => {
                    const isSubmenu = item.type === MENU_ITEM_TYPE.SUBMENU;
                    const isToggle = item.type === MENU_ITEM_TYPE.TOGGLE;
                    const isMcpItem = item.key === MENU_ITEM_KEY.AI_CHAT_MENU_MCP_SETTING;

                    const isKibanaMcpItem = item.key === MENU_ITEM_KEY.AI_CHAT_MENU_KIBANA_ANALYSIS;

                    return (
                        <MenuItem
                            key={item.key}
                            ref={isMcpItem ? mcpMenuItemRef : null}
                            {...(isKibanaMcpItem && item.selected ? { selected: true } : {})}
                            onClick={(e) => handleMenuItemClick(item, e)}
                            className={`ai-chat-menu-item ${isSubmenu ? 'ai-chat-menu-item-submenu' : ''} ${isToggle ? 'ai-chat-menu-item-toggle' : ''}`}
                            style={isKibanaMcpItem && item.selected ? { backgroundColor: 'rgb(232, 241, 255)' } : undefined}
                        >
                            <span className="menu-item-icon">{item.icon}</span>
                            <span className="menu-item-label">{trI18n(item?.label || item.key)}</span>
                            {isSubmenu && <ChevronRightIcon className="submenu-arrow" />}
                            {isToggle && (
                                <div
                                    className="switch-check menu-item-toggle-switch"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        id={`switch-${item.key}`}
                                        checked={item.checked}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleMenuItemClick(item, e);
                                        }}
                                    />
                                    <label htmlFor={`switch-${item.key}`}></label>
                                </div>
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>

            {/* MCP Server Submenu */}
            <MCPServerMenu
                anchorEl={subMenuAnchorEl}
                open={isSubMenuOpen}
                onClose={handleCloseSubMenu}
                serverList={serverList}
                selectedServers={selectedMcpServers}
                onToggleServer={selectedServerId =>
                    handleToggleMcpServer({selectedServerId, selectedMcpServers, onMcpSelectionChange})
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                menuStyle={{ marginLeft: 4 }}
            />
        </div>
    );
}

export default InputChatAiGroupButtons;
